import React, { memo, useCallback, useEffect, useState } from 'react';
import { areEqual } from 'Utils/equalityChecks';
import { useDispatch, useSelector } from 'react-redux';

import { ExternalAtmosphericTable } from 'Components/RocketDry';
import { SpinnerBlock } from 'Components/SpinnerBlock';

import { externalAtmosphericTableHeaders } from 'Utils/table';

import {
  getProjectAtmosphericLogsWithPhoto,
  setExternalAtmosphericLogCreated,
  setExternalAtmosphericLogUpdated,
  setExternalAtmosphericLogDeleted,
} from 'Containers/RocketDry/actions';

import { projectSelector } from 'Containers/RocketScan/selectors';
import {
  projectAtmosphericLogsSelector,
  externalAtmosphericLogsCreatedSelector,
  externalAtmosphericLogsUpdatedSelector,
  externalAtmosphericLogsDeletedSelector,
} from 'Containers/RocketDry/selectors';
import { atmosphericLogType, ExternalAtmosphericLog } from 'Containers/RocketDry/types';
import { useRocketDryFunctions } from 'Context/RocketDry';

function ExternalAtmosphericTableContainer() {
  const dispatch = useDispatch();

  const {
    setSelectedAtmosphericLog,
    setSelectedAtmosphericLogType,
    setIsViewAtmosphericLogModalOpen,
    setIsAddExternalAtmosphericLogModalOpen,
    setIsEditExternalAtmosphericLogModalOpen,
    setIsDeleteAtmosphericLogModalOpen,
  }: any = useRocketDryFunctions();

  // selectors
  const project = useSelector(projectSelector, areEqual);
  const atmosphericLogs = useSelector(projectAtmosphericLogsSelector, areEqual);
  const externalAtmosphericLogsCreated = useSelector(externalAtmosphericLogsCreatedSelector, areEqual);
  const externalAtmosphericLogsUpdated = useSelector(externalAtmosphericLogsUpdatedSelector, areEqual);
  const externalAtmosphericLogsDeleted = useSelector(externalAtmosphericLogsDeletedSelector, areEqual);

  // local variables
  const [fetching, setFetching] = useState(false);

  const handleViewPicture = useCallback((log: any) => {
    setSelectedAtmosphericLog(log);
    setSelectedAtmosphericLogType(atmosphericLogType.External);
    setIsViewAtmosphericLogModalOpen(true);
  }, []);

  const handleAddLog = useCallback(() => {
    setIsAddExternalAtmosphericLogModalOpen(true);
  }, []);

  const handleEditLog = useCallback((log: ExternalAtmosphericLog) => {
    setSelectedAtmosphericLog(log);
    setIsEditExternalAtmosphericLogModalOpen(true);
  }, []);

  const handleDeleteLog = useCallback((log: ExternalAtmosphericLog) => {
    setSelectedAtmosphericLog(log);
    setIsDeleteAtmosphericLogModalOpen(true);
  }, []);

  const getExternalAtmosphericLogs = useCallback(() => {
    const { id: projectId } = project;

    dispatch(getProjectAtmosphericLogsWithPhoto(projectId, setFetching));
  }, [project]);

  useEffect(() => {
    if (externalAtmosphericLogsCreated) {
      dispatch(setExternalAtmosphericLogCreated(false));
      getExternalAtmosphericLogs();
    }
  }, [externalAtmosphericLogsCreated]);

  useEffect(() => {
    if (externalAtmosphericLogsUpdated) {
      dispatch(setExternalAtmosphericLogUpdated(false));
      getExternalAtmosphericLogs();
    }
  }, [externalAtmosphericLogsUpdated]);

  useEffect(() => {
    if (externalAtmosphericLogsDeleted) {
      dispatch(setExternalAtmosphericLogDeleted(false));
      getExternalAtmosphericLogs();
    }
  }, [externalAtmosphericLogsDeleted]);

  useEffect(() => {
    if (project?.id) {
      getExternalAtmosphericLogs();
    }
  }, [project]);

  return (
    <>
      <SpinnerBlock fetching={fetching} />
      {!fetching && (
        <ExternalAtmosphericTable
          headers={externalAtmosphericTableHeaders}
          logs={atmosphericLogs}
          handleViewPicture={handleViewPicture}
          handleAddLog={handleAddLog}
          handleEditLog={handleEditLog}
          handleDeleteLog={handleDeleteLog}
        />
      )}
    </>
  );
}

const ExternalAtmosphericTableContainerMemo = memo(ExternalAtmosphericTableContainer, areEqual);

export { ExternalAtmosphericTableContainerMemo as ExternalAtmosphericTable };
